import * as React from "react";
import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import { Tracker } from "~utils/tracker";

import { accessibleOnClick } from "../constants";

import styles from "./styles.module.css";

const MIN_SWIPE_DISTANCE = 50;

const tracker = new Tracker({
    event: "Takeaways",
    action: "click",
});

const trackCarouselDot = (label: "click-fwd" | "click-back") => {
    tracker.trackEvent({
        category: "takeaways-nav",
        label,
    });
};

type DotProps = {
    onClick: (index: number) => void;
    index: number;
    isActive: boolean;
};

function Dot({ onClick, index, isActive }: DotProps) {
    const handleOnClick = useCallback(() => {
        onClick(index);
    }, [onClick, index]);

    return (
        <span
            className={createClasses([
                styles.dot,
                isActive && styles.activeDot,
            ])}
            aria-label="navigation dot"
            {...accessibleOnClick(handleOnClick)}
        />
    );
}

type MobileCarouselProps<IterableComponentProps = any> = {
    IterableComponent: React.ComponentType<IterableComponentProps>;
    componentPropsList: IterableComponentProps[];
};

export const MobileCarousel = ({
    IterableComponent,
    componentPropsList,
}: MobileCarouselProps) => {
    const autoadvancing = useRef<NodeJS.Timeout | null>(null);
    const [currentCard, setCurrentCard] = useState(0);
    const touchStart = useRef<number | null>(null);
    const touchEnd = useRef<number | null>(null);
    const cardRefs = useRef<(HTMLSpanElement | null)[]>([]);
    const [carouselHeight, setCarouselHeight] = useState<number | undefined>(
        undefined
    );

    const items = useMemo(() => componentPropsList, [componentPropsList]);

    const autoadvance = useCallback((interval: number, cardCount: number) => {
        autoadvancing.current = setInterval(() => {
            setCurrentCard((prevCard) => (prevCard + 1) % cardCount);
        }, interval);
    }, []);

    const stopAutoadvance = useCallback(() => {
        if (autoadvancing.current) {
            clearInterval(autoadvancing.current);
            autoadvancing.current = null;
        }
    }, []);

    const resetAutoadvance = useCallback(() => {
        stopAutoadvance();
    }, [items.length, autoadvance, stopAutoadvance]);

    useEffect(
        () => stopAutoadvance,
        [items.length, autoadvance, stopAutoadvance]
    );

    useEffect(() => {
        if (!cardRefs.current.length) return;
        const heights = cardRefs.current.map((ref) => ref?.offsetHeight || 0);
        const maxHeight = Math.max(...heights);
        setCarouselHeight(maxHeight);
    }, [componentPropsList]);

    const navigateLeft = useCallback(() => {
        setCurrentCard((prevCard) => Math.max(prevCard - 1, 0));

        resetAutoadvance();
    }, [resetAutoadvance]);

    const navigateRight = useCallback(() => {
        setCurrentCard((prevCard) => Math.min(prevCard + 1, items.length - 1));

        resetAutoadvance();
    }, [resetAutoadvance, items.length]);

    const navigateToCard = useCallback(
        (nextCurrentCard: number) => {
            if (nextCurrentCard < currentCard) {
                trackCarouselDot("click-back");
            } else if (nextCurrentCard > currentCard) {
                trackCarouselDot("click-fwd");
            }

            setCurrentCard(nextCurrentCard);

            resetAutoadvance();
        },
        [resetAutoadvance]
    );

    const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        touchEnd.current = null;
        touchStart.current = e.targetTouches[0].clientX;
    };

    const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        touchEnd.current = e.targetTouches[0].clientX;
    };

    const onTouchEnd = () => {
        if (touchStart.current === null || touchEnd.current === null) return;

        const distance = touchEnd.current - touchStart.current;
        const swipeLeftToRight = distance > MIN_SWIPE_DISTANCE;
        const swipeRightToLeft = distance < -MIN_SWIPE_DISTANCE;

        if (swipeLeftToRight) {
            navigateLeft();
        }

        if (swipeRightToLeft) {
            navigateRight();
        }
    };

    if (!items.length) {
        return <></>;
    }

    return (
        <>
            <div
                className={styles.animatedBox}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                style={
                    carouselHeight
                        ? {
                              height: `${carouselHeight}px`,
                          }
                        : undefined
                }
            >
                {items.map((item, index) => {
                    const cx = createClasses([
                        styles.animatedCard,
                        index < currentCard && styles.prevCard,

                        index > currentCard && styles.nextCard,
                    ]);
                    return (
                        <span
                            className={cx}
                            key={index}
                            ref={(el) => {
                                cardRefs.current[index] = el;
                            }}
                        >
                            <IterableComponent {...item} />
                        </span>
                    );
                })}
            </div>
            <div className={styles.mobileCarousel}>
                <div className={styles.navSection}>
                    {items.map((_, index) => (
                        <Dot
                            key={index}
                            isActive={index === currentCard}
                            onClick={navigateToCard}
                            index={index}
                        />
                    ))}
                </div>
            </div>
        </>
    );
};
